import type { T_Maybe } from '@tryiris-ai/js-types';
import Emittery from 'emittery';
import type { ConnectionOptions, NatsConnection, Status } from 'nats.ws';
import { connect, jwtAuthenticator } from 'nats.ws';
import { NatsTimeout } from './constants.js';
import { getNatsToken } from './utils.js';
import { config } from '#~/config/index.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';
import { NewtsConnection } from '#~/newts/connection.js';
import type { LiveKitImpl } from '#~/livekit/livekit.js';

/** NATS connection proxy. */
export const proxy = new (class extends Emittery<{ closed: undefined; status: Status }> {
  #connection: T_Maybe<NatsConnection>;
  #newts: Map<string, T_Maybe<NewtsConnection>> = new Map();

  public get natsConnection(): NatsConnection {
    return this.#connection!;
  }

  public get isConnected(): boolean {
    return !!this.#connection; //|| !!this.#newts;
  }

  #livekit: T_Maybe<LiveKitImpl>;

  public newtsConnectionForAgentId(agentId: string): NewtsConnection {
    if (agentId === undefined) {
      throw new StudioError('Agent ID is required.');
    }

    if (!this.#newts.has(agentId)) {
      this.#newts.set(agentId, new NewtsConnection(agentId));

      if (this.#livekit) {
        this.#newts.get(agentId)!.init(this.#livekit);
      }
    }

    return this.#newts.get(agentId)!;
  }

  public async connectNewts(agentId: string, livekit: LiveKitImpl): Promise<void> {
    this.#livekit = livekit;

    this.#newts.get(agentId)?.init(livekit);
  }

  public get isLocal(): boolean {
    for (const newts of this.#newts.values()) {
      if (newts?.connectedMode === 'local') {
        return true;
      }
    }

    return false;
  }

  public get localModeCapable(): boolean {
    for (const newts of this.#newts.values()) {
      if (newts?.localModeCapable) {
        return true;
      }
    }

    return false;
  }

  public async connectNats(accessToken: T_Maybe<string>): Promise<void> {
    // Only connect once.
    if (this.isConnected) {
      return;
    }

    const opts: ConnectionOptions = {
      servers: config.nats.endpoint,
      timeout: NatsTimeout
    };

    if (accessToken) {
      const { seed, token: jwt } = await getNatsToken(accessToken, opts);

      opts.authenticator = jwtAuthenticator(jwt, new TextEncoder().encode(seed));
    }

    opts.reconnect = true;
    opts.reconnectTimeWait = 2e3;
    opts.pingInterval = 5e3;

    // Never give up, never surrender.
    opts.maxReconnectAttempts = -1;

    this.#connection = await connect(opts);

    const connection = this.#connection;

    /* eslint-disable @typescript-eslint/no-floating-promises, promise/catch-or-return */
    connection.closed().then((cause) => {
      this.#connection = undefined;

      if (cause) {
        logger.error(new StudioError('NATS connection closed with error.', { cause }));
      }

      this.emit('closed').catch((error_) => {
        logger.critical(error_);
      });
    });

    (async (): Promise<void> => {
      for await (const status of connection.status()) {
        this.emit('status', status).catch((error_) => {
          logger.error(error_);
        });
      }
    })();
    /* eslint-enable @typescript-eslint/no-floating-promises, promise/catch-or-return */
  }

  public async disconnect(): Promise<void> {
    const connection = this.#connection;

    if (connection) {
      this.#connection = undefined;
      await connection.close();
    }
  }
})();
